import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete, Avatar, Box, Button, Chip, CircularProgress, Grid, MenuItem, Stack, Tab, Tabs,
  TextField, Typography,
} from "@mui/material";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import api from "../../api/client";
import { endpoints } from "../../api/endpoints";
import { PageHeader, SectionCard, StatusChip, EmployeeSelect } from "../ui";
import { extractErrorMessage } from "../../utils/errorMessage";
import { sortByLabel } from "../../utils/sortOptions";
import { useAuth } from "../../context/AuthContext";

const ROLES = ["employee", "manager", "hr", "admin", "storemanager"];
const WORK_MODES = ["onsite", "hybrid", "remote"];

function Info({ label, value }) {
  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, fontSize: 10.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 500, wordBreak: "break-word" }}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

export default function EmployeeDetailNew() {
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const { hasRole, hasAccess, user } = useAuth();
  const canEdit = hasRole("admin", "hr") || hasAccess("critical");

  const [employees, setEmployees] = useState([]);
  const [employee, setEmployee] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);

  const queryId = sp.get("id") || sp.get("employeeid");
  const canSeeCompensation = hasAccess("critical") || (user?.employeeid && employee?.employeeid === user.employeeid);
  const leaveTab = canSeeCompensation ? 2 : 1;

  useEffect(() => {
    api.get(endpoints.employees.list).then((res) => {
      setEmployees(Array.isArray(res.data) ? res.data : res.data?.data || []);
    }).catch(() => setEmployees([]));
  }, []);

  useEffect(() => {
    if (!queryId) { setEmployee(null); return; }
    setLoading(true);
    api.get(`/api/employees/${queryId}`)
      .then((res) => {
        setEmployee(res.data);
        setDraft(res.data);
        setEditing(false);
      })
      .catch((err) => toast.error(extractErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [queryId]);

  const selectOpts = useMemo(
    () => sortByLabel(
      employees.map((e) => ({ id: e.employeeid || e._id, label: `${e.name} (${e.employeeid})`, ref: e })),
      (o) => o.label
    ),
    [employees]
  );

  const handleSelect = (opt) => {
    if (!opt) return;
    setSp({ id: opt.id });
  };

  const updateField = (k, v) => {
    if (k in (draft?.leaveBalance || {})) {
      setDraft((d) => ({ ...d, leaveBalance: { ...d.leaveBalance, [k]: v } }));
    } else if (k in (draft?.remainingLeave || {})) {
      setDraft((d) => ({ ...d, remainingLeave: { ...d.remainingLeave, [k]: v } }));
    } else {
      setDraft((d) => ({ ...d, [k]: v }));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/api/employees/${queryId}`, draft);
      setEmployee(res.data.updatedEmployee || res.data);
      setEditing(false);
      toast.success("Employee details updated");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally { setSaving(false); }
  };

  return (
    <Box p={3}>
      <PageHeader
        title="Employee Details"
        subtitle="View and manage employee records"
        icon={<PersonSearchOutlinedIcon />}
        actions={[
          <Autocomplete
            key="search"
            size="small"
            options={selectOpts}
            onChange={(_, v) => handleSelect(v)}
            sx={{ width: { xs: "100%", sm: 320 } }}
            renderInput={(params) => (
              <TextField {...params} placeholder="Search employee…" />
            )}
          />,
        ]}
      />

      {!queryId && (
        <SectionCard>
          <Box textAlign="center" py={6}>
            <PersonSearchOutlinedIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1.5 }} />
            <Typography variant="h6" fontWeight={700} mb={0.5}>Find an employee</Typography>
            <Typography color="text.secondary" fontSize={13}>
              Use the search above to view or edit an employee's profile.
            </Typography>
          </Box>
        </SectionCard>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {employee && !loading && (
        <>
          <Box
            sx={{
              p: 3, mb: 3, borderRadius: 4,
              background: "linear-gradient(135deg, #6C50BF 0%, #8568D4 100%)",
              color: "#fff", position: "relative", overflow: "hidden",
            }}
          >
            <Box sx={{ position: "absolute", right: -60, top: -60, width: 200, height: 200, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.08)" }} />
            <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems={{ xs: "flex-start", md: "center" }} sx={{ position: "relative" }}>
              <Avatar
                src={employee.profilePicture}
                sx={{ width: 88, height: 88, fontSize: 32, fontWeight: 700, border: "4px solid rgba(255,255,255,0.4)", bgcolor: "#fff", color: "primary.main" }}
              >
                {(employee.name || "U").slice(0, 1).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5} flexWrap="wrap" useFlexGap>
                  <Typography sx={{ fontSize: 22, fontWeight: 800 }}>{employee.name}</Typography>
                  <StatusChip status={employee.employmentStatus || "active"} />
                </Stack>
                <Stack direction="row" spacing={1} mb={1.5} flexWrap="wrap" useFlexGap>
                  <Chip label={employee.employeeid} size="small" sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 600 }} />
                  <Chip label={employee.role} size="small" sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 600, textTransform: "capitalize" }} />
                  {employee.department && <Chip label={employee.department} size="small" sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 600 }} />}
                </Stack>
                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                  {employee.email && (
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <EmailOutlinedIcon sx={{ fontSize: 15, opacity: 0.85 }} />
                      <Typography sx={{ fontSize: 13, opacity: 0.95 }}>{employee.email}</Typography>
                    </Stack>
                  )}
                  {employee.contactNumber && (
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <PhoneOutlinedIcon sx={{ fontSize: 15, opacity: 0.85 }} />
                      <Typography sx={{ fontSize: 13, opacity: 0.95 }}>{employee.contactNumber}</Typography>
                    </Stack>
                  )}
                  {employee.dateOfJoining && (
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <CalendarMonthOutlinedIcon sx={{ fontSize: 15, opacity: 0.85 }} />
                      <Typography sx={{ fontSize: 13, opacity: 0.95 }}>
                        Joined {new Date(employee.dateOfJoining).toLocaleDateString("en-IN", { month: "short", day: "numeric", year: "numeric" })}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>
              {canEdit && !editing && (
                <Button
                  variant="contained"
                  startIcon={<EditOutlinedIcon />}
                  onClick={() => setEditing(true)}
                  sx={{ bgcolor: "#fff", color: "primary.main", fontWeight: 700, "&:hover": { bgcolor: "grey.100" } }}
                >
                  Edit
                </Button>
              )}
              {editing && (
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveOutlinedIcon />}
                    onClick={save}
                    disabled={saving}
                    sx={{ bgcolor: "#39C645", "&:hover": { bgcolor: "#2fa038" }, fontWeight: 700 }}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CloseOutlinedIcon />}
                    onClick={() => { setDraft(employee); setEditing(false); }}
                    sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)", "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.1)" } }}
                  >
                    Cancel
                  </Button>
                </Stack>
              )}
            </Stack>
          </Box>

          <Box sx={{ borderBottom: "1px solid", borderColor: "divider", mb: 3 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              <Tab label="Profile" />
              {canSeeCompensation && <Tab label="Compensation" />}
              <Tab label="Leave Balance" />
            </Tabs>
          </Box>

          {tab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <SectionCard title="Personal" accent="#6C50BF">
                  {editing ? (
                    <Stack spacing={2}>
                      <TextField fullWidth size="small" label="Name" value={draft.name || ""} onChange={(e) => updateField("name", e.target.value)} />
                      <TextField fullWidth size="small" label="Email" type="email" value={draft.email || ""} onChange={(e) => updateField("email", e.target.value)} />
                      <TextField fullWidth size="small" label="Contact Number" value={draft.contactNumber || ""} onChange={(e) => updateField("contactNumber", e.target.value)} />
                      <TextField fullWidth size="small" label="Date of Birth" type="date" InputLabelProps={{ shrink: true }} value={draft.dateOfBirth ? draft.dateOfBirth.slice(0, 10) : ""} onChange={(e) => updateField("dateOfBirth", e.target.value)} />
                      {canSeeCompensation && (
                        <>
                          <TextField fullWidth size="small" label="PAN Number" value={draft.panNumber || ""} onChange={(e) => updateField("panNumber", e.target.value.toUpperCase())} />
                          <TextField fullWidth size="small" label="Aadhar Number" value={draft.aadharNumber || ""} onChange={(e) => updateField("aadharNumber", e.target.value)} />
                        </>
                      )}
                    </Stack>
                  ) : (
                    <Grid container>
                      <Grid item xs={6}><Info label="Employee ID" value={employee.employeeid} /></Grid>
                      <Grid item xs={6}><Info label="Email" value={employee.email} /></Grid>
                      <Grid item xs={6}><Info label="Contact" value={employee.contactNumber} /></Grid>
                      <Grid item xs={6}><Info label="Date of Birth" value={employee.dateOfBirth ? new Date(employee.dateOfBirth).toLocaleDateString("en-IN") : null} /></Grid>
                      {canSeeCompensation && (
                        <>
                          <Grid item xs={6}><Info label="PAN" value={employee.panNumber} /></Grid>
                          <Grid item xs={6}><Info label="Aadhar" value={employee.aadharNumber} /></Grid>
                        </>
                      )}
                    </Grid>
                  )}
                </SectionCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <SectionCard title="Employment" accent="#39C645">
                  {editing ? (
                    <Stack spacing={2}>
                      <TextField fullWidth size="small" select label="Role" value={draft.role || ""} onChange={(e) => updateField("role", e.target.value)}>
                        {sortByLabel(ROLES).map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                      </TextField>
                      <TextField fullWidth size="small" label="Designation" value={draft.designation || ""} onChange={(e) => updateField("designation", e.target.value)} />
                      <TextField fullWidth size="small" label="Department" value={draft.department || ""} onChange={(e) => updateField("department", e.target.value)} />
                      <EmployeeSelect
                        label="Reporting Manager"
                        value={draft.manager || ""}
                        onChange={(id) => updateField("manager", id)}
                        managersOnly
                        excludeIds={[employee.employeeid]}
                        helperText="Select who this employee reports to"
                      />
                      <TextField fullWidth size="small" label="Date of Joining" type="date" InputLabelProps={{ shrink: true }} value={draft.dateOfJoining ? draft.dateOfJoining.slice(0, 10) : ""} onChange={(e) => updateField("dateOfJoining", e.target.value)} />
                      <TextField fullWidth size="small" label="Job Location" value={draft.jobLocation || ""} onChange={(e) => updateField("jobLocation", e.target.value)} />
                      <TextField fullWidth size="small" select label="Work Mode" value={draft.workMode || "hybrid"} onChange={(e) => updateField("workMode", e.target.value)}>
                        {sortByLabel(WORK_MODES).map((w) => <MenuItem key={w} value={w}>{w}</MenuItem>)}
                      </TextField>
                    </Stack>
                  ) : (
                    <Grid container>
                      <Grid item xs={6}><Info label="Role" value={employee.role} /></Grid>
                      <Grid item xs={6}><Info label="Designation" value={employee.designation} /></Grid>
                      <Grid item xs={6}><Info label="Department" value={employee.department} /></Grid>
                      <Grid item xs={6}><Info label="Reporting Manager" value={employee.managerName ? `${employee.managerName} (${employee.manager})` : employee.manager} /></Grid>
                      <Grid item xs={6}><Info label="Date of Joining" value={employee.dateOfJoining ? new Date(employee.dateOfJoining).toLocaleDateString("en-IN") : null} /></Grid>
                      <Grid item xs={6}><Info label="Job Location" value={employee.jobLocation} /></Grid>
                      <Grid item xs={6}><Info label="Work Mode" value={employee.workMode} /></Grid>
                      <Grid item xs={6}><Info label="Status" value={employee.employmentStatus || "active"} /></Grid>
                    </Grid>
                  )}
                </SectionCard>
              </Grid>
            </Grid>
          )}

          {canSeeCompensation && tab === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <SectionCard title="Salary & Tax" accent="#F9BC06">
                  {editing ? (
                    <Stack spacing={2}>
                      <TextField fullWidth size="small" type="number" label="Annual CTC (₹)" value={draft.offeredSalary || ""} onChange={(e) => updateField("offeredSalary", e.target.value)} />
                      <TextField fullWidth size="small" type="number" label="TDS (Monthly ₹)" value={draft.TDSAmount || ""} onChange={(e) => updateField("TDSAmount", e.target.value)} />
                    </Stack>
                  ) : (
                    <Grid container>
                      <Grid item xs={6}><Info label="Annual CTC" value={employee.offeredSalary ? `₹${Number(employee.offeredSalary).toLocaleString("en-IN")}` : null} /></Grid>
                      <Grid item xs={6}><Info label="TDS (Monthly)" value={employee.TDSAmount ? `₹${Number(employee.TDSAmount).toLocaleString("en-IN")}` : null} /></Grid>
                      <Grid item xs={6}><Info label="Monthly Gross" value={employee.offeredSalary ? `₹${Number(employee.offeredSalary / 12).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : null} /></Grid>
                    </Grid>
                  )}
                </SectionCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <SectionCard title="Bank Details" accent="#5C85FF">
                  {editing ? (
                    <Stack spacing={2}>
                      <TextField fullWidth size="small" label="Bank Name" value={draft.bankName || ""} onChange={(e) => updateField("bankName", e.target.value)} />
                      <TextField fullWidth size="small" label="Account Number" value={draft.accountNumber || ""} onChange={(e) => updateField("accountNumber", e.target.value)} />
                      <TextField fullWidth size="small" label="IFSC Code" value={draft.ifscCode || ""} onChange={(e) => updateField("ifscCode", e.target.value.toUpperCase())} />
                    </Stack>
                  ) : (
                    <Grid container>
                      <Grid item xs={6}><Info label="Bank Name" value={employee.bankName} /></Grid>
                      <Grid item xs={6}><Info label="Account Number" value={employee.accountNumber} /></Grid>
                      <Grid item xs={6}><Info label="IFSC" value={employee.ifscCode} /></Grid>
                    </Grid>
                  )}
                </SectionCard>
              </Grid>
            </Grid>
          )}

          {tab === leaveTab && (
            <SectionCard title="Leave Balance" subtitle="Annual allocation and remaining days" accent="#5C85FF">
              <Grid container spacing={2}>
                {[
                  { key: "annualLeave", label: "Annual Leave", color: "#6C50BF" },
                  { key: "sickLeave", label: "Sick Leave", color: "#F9BC06" },
                  { key: "casualleave", label: "Casual Leave", color: "#39C645" },
                ].map((l) => {
                  const total = employee.leaveBalance?.[l.key] ?? employee[l.key] ?? 0;
                  const remaining = employee.remainingLeave?.[l.key] ?? total;
                  const used = Math.max(total - remaining, 0);
                  return (
                    <Grid item xs={12} sm={6} md={4} key={l.key}>
                      <Box sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{l.label}</Typography>
                          <Chip size="small" label={`${remaining} left`} sx={{ bgcolor: `${l.color}15`, color: l.color, fontWeight: 700 }} />
                        </Stack>
                        {editing ? (
                          <Stack direction="row" spacing={1}>
                            <TextField
                              size="small" type="number" label="Total"
                              value={total}
                              onChange={(e) => setDraft((d) => ({ ...d, leaveBalance: { ...d.leaveBalance, [l.key]: Number(e.target.value) } }))}
                            />
                            <TextField
                              size="small" type="number" label="Remaining"
                              value={remaining}
                              onChange={(e) => setDraft((d) => ({ ...d, remainingLeave: { ...d.remainingLeave, [l.key]: Number(e.target.value) } }))}
                            />
                          </Stack>
                        ) : (
                          <>
                            <Typography sx={{ fontSize: 24, fontWeight: 800, color: l.color, lineHeight: 1.1 }}>{used}</Typography>
                            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>of {total} days used</Typography>
                            <Box sx={{ height: 6, borderRadius: 3, bgcolor: "action.hover", mt: 1, overflow: "hidden" }}>
                              <Box sx={{ width: `${total > 0 ? (used / total) * 100 : 0}%`, height: "100%", bgcolor: l.color }} />
                            </Box>
                          </>
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </SectionCard>
          )}
        </>
      )}
    </Box>
  );
}
